export type EngineScore = { type: "cp"; value: number } | { type: "mate"; value: number };

export interface EvalThresholds {
  blunderWinPercentDrop: number;
  inaccuracyWinPercentDrop: number;
  missWinPercentDrop: number;
}

const WIN_PERCENT_SCALE = 0.00368208;
const ACCURACY_INTERCEPT = 103.1668;
const ACCURACY_SLOPE = 0.04354;
const ACCURACY_OFFSET = 3.1669;
const MATE_SCORE = 1;

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function cpToWinPercent(cp: number): number {
  if (Number.isNaN(cp)) return 50;
  const winPercent = 50 + 50 * (2 / (1 + Math.exp(-WIN_PERCENT_SCALE * cp)) - 1);
  return clamp(winPercent, 0, 100);
}

export function scoreToWinPercent(score: EngineScore, perspective: "white" | "black"): number {
  const whiteWinPercent =
    score.type === "cp"
      ? cpToWinPercent(score.value)
      : score.value > 0
        ? 100
        : score.value < 0
          ? 0
          : 50;
  return perspective === "white" ? whiteWinPercent : 100 - whiteWinPercent;
}

export function winPercentToScore(winPercent: number, perspective: "white" | "black"): EngineScore {
  const bounded = clamp(Number.isNaN(winPercent) ? 50 : winPercent, 0, 100);
  if (bounded >= 100) {
    return { type: "mate", value: perspective === "white" ? MATE_SCORE : -MATE_SCORE };
  }
  if (bounded <= 0) {
    return { type: "mate", value: perspective === "white" ? -MATE_SCORE : MATE_SCORE };
  }
  const normalized = bounded / 50 - 1;
  const cp = Math.log((1 + normalized) / (1 - normalized)) / WIN_PERCENT_SCALE;
  const signedCp = perspective === "white" ? cp : -cp;
  return { type: "cp", value: Math.round(signedCp) };
}

export function accuracyFromWinPercents(winPercents: number[]): number {
  if (winPercents.length < 2) return 100;
  let volatilitySum = 0;
  for (let index = 1; index < winPercents.length; index += 1) {
    const expected = clamp(winPercents[index - 1] ?? 50, 0, 100);
    const actual = clamp(winPercents[index] ?? 50, 0, 100);
    volatilitySum += Math.abs(actual - expected);
  }
  const volatility = volatilitySum / winPercents.length;
  const accuracy = ACCURACY_INTERCEPT * Math.exp(-ACCURACY_SLOPE * volatility) - ACCURACY_OFFSET;
  return clamp(Math.round(accuracy * 100) / 100, 0, 100);
}
